import { FormApi } from "final-form";
import { Form, useForm } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useDispatch, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { Modal } from "src/ui/shared/Modal/Modal";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { PrimaryButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { createProvisioning } from "../store";
import { Provisioning } from "../types";
import { defaultProvisioning } from "../utils";

type FormValues = Provisioning;

type Props = {
    open: boolean;
    onClose: () => void;
    onCreated: (provisioningId: number) => void;
};

const CreateProvisioningDialog = (props: Props) => {
    const dispatch = useDispatch();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const parentForm = useForm();
    const { t } = useTranslation(["modals", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const provisioningNames = provisionings.map((item) => item.name);

    const onValidate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.name) {
            errors.name = t("modals:createProvisioning.input.name.validation.empty");
        }
        if (provisioningNames.includes(values.name)) {
            errors.name = t("modals:createProvisioning.input.name.validation.unique");
        }

        return errors;
    };
    const onSubmit = async (values: FormValues) => {
        if (workspaceId) {
            const { name, settings } = values;
            await dispatch(createProvisioning({ name, workspaceId, settings }))
                .unwrap()
                .then((newProvisioning) => {
                    showSuccessToast(t("modals:createProvisioning.toast.provisioningCreatedSuccessfully"));
                    parentForm.initialize(newProvisioning);
                    props.onCreated(newProvisioning.id);
                })
                .catch(({ message = t("modals:createProvisioning.toast.provisioningCreationFailed") }) => showErrorToast(message))
                .finally(() => props.onClose());
        }
    };
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !form.getState().submitting) {
            void form.submit();
        }
        if (e.key === "Escape") {
            props.onClose();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={onValidate}
            initialValues={defaultProvisioning}
            render={({ handleSubmit, submitting, valid, form }) => (
                <Modal
                    title={t("modals:createProvisioning.title")}
                    onKeyDown={(e) => onKeyDown(e, form)}
                    open={props.open}
                    onClose={props.onClose}
                    endButton={
                        <PrimaryButton onClick={form.submit} disabled={submitting || !valid} loading={submitting}>
                            {t("common:button.create")}
                        </PrimaryButton>
                    }
                >
                    <form onSubmit={handleSubmit}>
                        <TextField label={t("modals:createProvisioning.input.name.caption")} fullWidth name="name" />
                    </form>
                </Modal>
            )}
        />
    );
};

export default CreateProvisioningDialog;
